// CSV Export Utility

/**
 * Convert array of objects to CSV string
 * @param {Array} data - Array of objects to convert
 * @param {Array} columns - Column definitions [{key: 'field', label: 'Header'}]
 * @returns {string} CSV formatted string
 */
export const convertToCSV = (data, columns) => {
  if (!data || data.length === 0) return '';
  
  // Create header row
  const headers = columns.map(col => `"${col.label}"`).join(',');
  
  // Create data rows
  const rows = data.map(row => {
    return columns.map(col => {
      let value = row[col.key];
      
      // Handle nested keys like 'user.name'
      if (col.key.includes('.')) {
        const keys = col.key.split('.');
        value = keys.reduce((obj, key) => obj?.[key], row);
      }
      
      // Format value
      if (value === null || value === undefined) {
        value = '';
      } else if (typeof value === 'number') {
        value = value.toString();
      } else if (typeof value === 'object') {
        value = JSON.stringify(value);
      }
      
      // Escape quotes and wrap in quotes
      return `"${String(value).replace(/"/g, '""')}"`;
    }).join(',');
  });
  
  return [headers, ...rows].join('\n');
};

/**
 * Download CSV file
 * @param {string} csvContent - CSV formatted string
 * @param {string} filename - Name of the file (without extension)
 */
export const downloadCSV = (csvContent, filename) => {
  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
};

/**
 * Export data to CSV and trigger download
 * @param {Array} data - Array of objects to export
 * @param {Array} columns - Column definitions
 * @param {string} filename - Filename without extension
 */
export const exportToCSV = (data, columns, filename) => {
  const csv = convertToCSV(data, columns);
  if (csv) {
    downloadCSV(csv, filename);
    return true;
  }
  return false;
};

// Predefined column configurations for different data types
export const EXPENSE_COLUMNS = [
  { key: 'date', label: 'Date' },
  { key: 'category', label: 'Category' },
  { key: 'sub_category', label: 'Sub Category' },
  { key: 'vendor_name', label: 'Vendor/Person' },
  { key: 'amount', label: 'Amount (₹)' },
  { key: 'payment_method', label: 'Payment Method' },
  { key: 'status', label: 'Status' },
  { key: 'submitted_by', label: 'Submitted By' },
  { key: 'notes', label: 'Notes' }
];

export const SILICA_LOAD_COLUMNS = [
  { key: 'date', label: 'Date' },
  { key: 'client_name', label: 'Client' },
  { key: 'vehicle_number', label: 'Vehicle Number' },
  { key: 'driver_name', label: 'Driver' },
  { key: 'driver_contact', label: 'Driver Contact' },
  { key: 'tons', label: 'Tons' },
  { key: 'invoice_amount', label: 'Invoice Amount (₹)' },
  { key: 'final_invoice_amount', label: 'Final Invoice (₹)' },
  { key: 'delivery_status', label: 'Delivery Status' },
  { key: 'acceptance_status', label: 'Acceptance Status' },
  { key: 'bill_status', label: 'Bill Status' },
  { key: 'date_sent', label: 'Date Sent' },
  { key: 'date_received', label: 'Date Received' }
];

export const EXPENSE_REPORT_COLUMNS = [
  { key: 'category', label: 'Category' },
  { key: 'amount', label: 'Amount (₹)' }
];

export const PROFIT_REPORT_COLUMNS = [
  { key: 'month', label: 'Month' },
  { key: 'income', label: 'Income (₹)' },
  { key: 'expenses', label: 'Expenses (₹)' },
  { key: 'profit', label: 'Profit (₹)' }
];

export const SALARY_REPORT_COLUMNS = [
  { key: 'month', label: 'Month' },
  { key: 'totalSalary', label: 'Total Salary (₹)' },
  { key: 'totalAdvance', label: 'Total Advance (₹)' },
  { key: 'totalOvertime', label: 'Total Overtime (₹)' },
  { key: 'totalNet', label: 'Net Payment (₹)' }
];
